<script>
  import { Handle, Position } from "@xyflow/svelte";
  import { formatTimeDelta } from "../../../../lib/util/formatfunctions.js";

  export let data;
  export let id;
  export let isConnectable = false;
  let detailMode = "metrics";

  function metric(key) {
    const value = data.metrics?.[key];
    return value !== null && value !== undefined && value !== "" && Number.isFinite(Number(value)) ? Number(value) : null;
  }

  function percentage(key) {
    const value = metric(key);
    return value === null ? "–" : `${Math.round(value * 100)}%`;
  }

  function startPercentage() {
    return data.startRate !== null && data.startRate !== undefined && data.startRate !== "" && Number.isFinite(Number(data.startRate))
      ? `${Math.round(Number(data.startRate) * 100)}%`
      : "–";
  }

  function duration(key) {
    const value = metric(key);
    return value === null ? "–" : formatTimeDelta(value);
  }

  function topApp() {
    const apps = data.metrics?.apps;
    if (!apps || typeof apps !== "object") return { name: "–", share: "–" };
    const entry = Object.entries(apps).sort((a, b) => Number(b[1]) - Number(a[1]))[0];
    return entry
      ? { name: entry[0], share: `${Math.round(Number(entry[1]) * 100)}%` }
      : { name: "–", share: "–" };
  }

  function togglePanel(mode, event) {
    event.preventDefault();
    event.stopPropagation();
    if (data.expanded && detailMode !== mode) {
      detailMode = mode;
      return;
    }
    detailMode = mode;
    data.onToggle?.(id);
  }
</script>

{#if !data.isStart}
  <Handle type="target" position={Position.Top} {isConnectable} class="preview-handle" />
{/if}

{#if data.isStart || data.isEnd}
  <div class:playback-active={data.playbackActive} class="endpoint" style:transform={`translateY(${data.visualShift || 0}px)`}>{data.label}</div>
{:else}
  <div
    class:expanded={data.expanded}
    class:playback-active={data.playbackActive}
    class="process-node"
    data-node-id={id}
    style={`transform:translateY(${data.visualShift || 0}px);background:${data.highlightColor || "#fff"};`}
  >
    <div class="node-main">
      <div class="initial">{String(data.label || "?").charAt(0).toUpperCase()}</div>
      <div class="content">
        <div class="title-row">
          <strong title={data.label}>{data.label}</strong>
          <span class="info" title="Start shows the share of visible cases that begin with this task. The remaining metrics are calculated from the associated recordings.">i</span>
        </div>
        <div class="summary-grid">
          <span><b>Start:</b> {startPercentage()}</span>
          <span class="app" title={topApp().name}><b>Top App:</b> {topApp().name} {topApp().share}</span>
          <span><b>Time:</b> {duration("avg_duration")}</span>
          <span><b>Weighted Time:</b> {duration("time_per_case")}</span>
        </div>
      </div>
      <div class="actions">
        <button type="button" class="connection-toggle" class:active={data.expanded && detailMode === "connections"} on:pointerdown|stopPropagation on:click={(event) => togglePanel("connections", event)} aria-label="Toggle object activity matches" title="Object activity matches">+</button>
        <button type="button" on:pointerdown|stopPropagation on:click={(event) => togglePanel("metrics", event)} aria-label="Toggle metrics">
          <img class:expanded={data.expanded && detailMode === "metrics"} src="/img/ui_icons/down_dark.svg" alt="" />
        </button>
      </div>
    </div>
    {#if data.expanded}
      <div class="details">
        {#if detailMode === "connections"}
          {#each (data.caseIdActivities || []).slice(0, 4) as activity}
            <span class="activity-hit" title={activity.name}>
              <b>{activity.name}</b><strong>{activity.count}</strong>
            </span>
          {:else}
            <span class="activity-hit empty"><b>No object activity matches</b></span>
          {/each}
        {:else}
          <span><b>Copy Share</b><strong>{percentage("copy_paste_rate")}</strong></span>
          <span><b>Text Time</b><strong>{percentage("text_share")}</strong></span>
          <span><b>Copy Time</b><strong>{percentage("copy_paste_time_share")}</strong></span>
          <span><b>Mouse Clicks</b><strong>{metric("clicks") === null ? "–" : Math.round(metric("clicks"))}</strong></span>
        {/if}
      </div>
    {/if}
  </div>
{/if}

{#if !data.isEnd}
  <Handle type="source" position={Position.Bottom} {isConnectable} class="preview-handle" />
{/if}

<style>
  :global(.preview-handle) {
    opacity: 0;
    border: 0;
    pointer-events: none;
  }

  .endpoint {
    box-sizing: border-box;
    width: 90px;
    height: 48px;
    display: flex;
    align-items: center;
    justify-content: center;
    border: 2px solid #1c1c20;
    border-radius: 10px;
    background: #000;
    color: white;
    font-size: 12px;
    font-weight: 600;
  }

  .process-node {
    box-sizing: border-box;
    width: 390px;
    height: 78px;
    overflow: hidden;
    border: 1px solid #1c1c20;
    border-radius: 9px;
    background: white;
    box-shadow: 0 2px 5px rgba(0, 0, 0, 0.12);
    color: #161616;
  }

  .process-node.expanded {
    height: 132px;
  }

  .process-node.playback-active,
  .endpoint.playback-active {
    border-color: #ff5f1f;
    box-shadow: 0 0 0 3px rgba(255, 95, 31, 0.22);
  }

  .node-main {
    height: 78px;
    display: grid;
    grid-template-columns: 48px minmax(0, 1fr) 48px;
  }

  .initial,
  .actions {
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .actions {
    flex-direction: column;
    gap: 4px;
  }

  .initial {
    margin: auto;
    width: 23px;
    height: 23px;
    border-radius: 50%;
    background: #1c1c20;
    color: white;
    font-size: 11px;
    font-weight: 700;
  }

  .actions .connection-toggle {
    width: 23px;
    height: 23px;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0;
    border: 1px solid #b8b8b8;
    border-radius: 50%;
    background: #fff;
    color: #1c1c20;
    font-size: 15px;
    line-height: 1;
    cursor: pointer;
  }

  .actions .connection-toggle.active,
  .actions .connection-toggle:hover {
    border-color: #1c1c20;
    background: #e8e8e8;
  }

  .content {
    min-width: 0;
    padding: 9px 10px 7px;
    border-left: 1px solid #d6d6d6;
    border-right: 1px solid #d6d6d6;
  }

  .title-row {
    display: flex;
    align-items: center;
    gap: 6px;
    min-width: 0;
    margin-bottom: 6px;
  }

  .title-row strong {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-size: 15px;
  }

  .info {
    flex: 0 0 17px;
    width: 17px;
    height: 17px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border: 1.5px solid #1c1c20;
    border-radius: 50%;
    font-size: 10px;
    font-weight: 700;
  }

  .summary-grid {
    display: grid;
    grid-template-columns: 1fr 1.35fr;
    gap: 5px 12px;
    font-size: 10px;
  }

  .summary-grid span {
    min-width: 0;
    white-space: nowrap;
  }

  .summary-grid .app {
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .actions button {
    width: 23px;
    height: 23px;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0;
    border: 1px solid #b8b8b8;
    border-radius: 50%;
    background: #f4f4f4;
    cursor: pointer;
  }

  .actions button:hover {
    border-color: #1c1c20;
    background: #e8e8e8;
  }

  .actions img {
    display: block;
    width: 14px;
    height: 14px;
    transition: transform 0.15s ease;
  }

  .actions img.expanded {
    transform: rotate(180deg);
  }

  .details {
    position: relative;
    box-sizing: border-box;
    height: 54px;
    display: grid;
    grid-template-columns: 1fr 1fr;
    row-gap: 8px;
    align-items: center;
    padding: 10px 16px 12px;
    border-top: 1px solid #d6d6d6;
    background: #fbfbfb;
  }

  .details span {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    padding: 0;
    font-size: 10px;
  }

  .details span:nth-child(odd) {
    padding-right: 12px;
  }

  .details span:nth-child(even) {
    padding-left: 12px;
  }

  .details .activity-hit b {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .details .activity-hit.empty {
    grid-column: 1 / -1;
  }

  .details::after {
    content: "";
    position: absolute;
    top: 6px;
    bottom: 6px;
    left: 50%;
    width: 1px;
    background: #e0e0e0;
  }
</style>
